import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ContentData } from './types';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { 
  Video, 
  Link as LinkIcon, 
  Image, 
  Underline, 
  Table, 
  Quote,
  File,
  Search
} from 'lucide-react';
import CustomYouTubePlayer from "@/components/ui/CustomYouTubePlayer";
import CustomImageViewer from "@/components/ui/CustomImageViewer";
import ReactDOM from 'react-dom/client';
import { fileService, FileInfo } from '@/services/api';

interface TextEditorProps {
  content: string;
  onChange: (content: string) => void;
}

const TextEditor: React.FC<TextEditorProps> = ({ content, onChange }) => {
  const [videoTitle, setVideoTitle] = useState('');
  const [youtubeVideoId, setYoutubeVideoId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setIsLoading(true);
        const filesList = await fileService.listFiles();
        setFiles(filesList);
      } catch (error) {
        console.error('Error loading files:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFiles();
  }, []);
  
  const filteredFiles = files.filter(file => 
    file.originalName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const insertMarkdown = (markdown: string) => {
    onChange(content + markdown);
  };
  
  // Extract YouTube video ID from a URL
  const getYouTubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const insertVideo = () => {
    if (youtubeVideoId) {
      const videoUrl = `https://www.youtube.com/watch?v=${youtubeVideoId}`;
      const videoMarkdown = `!video [${videoTitle || 'Video'}](${videoUrl})`;
      insertMarkdown(videoMarkdown);
      setYoutubeVideoId('');
      setVideoTitle('');
    }
  };

  const insertImage = () => {
    if (imageUrl) {
      const imageMarkdown = `\n\n![${imageAlt || 'Image'}](${imageUrl})\n\n`;
      insertMarkdown(imageMarkdown);
      setImageUrl('');
      setImageAlt('');
    }
  };

  const insertLink = () => {
    if (linkUrl) {
      const linkMarkdown = `[${linkText || linkUrl}](${linkUrl})`;
      insertMarkdown(linkMarkdown);
      setLinkUrl('');
      setLinkText('');
    }
  };

  const insertFile = (file: FileInfo) => {
    const markdownLink = fileService.getMarkdownLink(file);
    insertMarkdown(markdownLink);
  };

  const insertTable = () => {
    const tableTemplate = `
| Header 1 | Header 2 | Header 3 |
|----------|----------|----------|
| Cell 1   | Cell 2   | Cell 3   |
| Cell 4   | Cell 5   | Cell 6   |
`;
    insertMarkdown(tableTemplate);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="textContent">Text Content</Label>
      <div className="border rounded-md p-2">
        <div className="mb-2 flex flex-wrap gap-2 border-b pb-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('# Heading 1\n')}
            className="h-8"
          >
            H1
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('## Heading 2\n')}
            className="h-8"
          >
            H2
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('### Heading 3\n')}
            className="h-8"
          >
            H3
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('**Bold Text**')}
            className="h-8 font-bold"
          >
            B
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('*Italic Text*')}
            className="h-8 italic"
          >
            I
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('__Underlined Text__')}
            className="h-8"
          >
            <Underline className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('```\nCode Block\n```')}
            className="h-8 font-mono"
          >
            Code
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('- List item\n')}
            className="h-8"
          >
            • List
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => insertMarkdown('\n> Blockquote text here\n')}
            className="h-8"
          >
            <Quote className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={insertTable}
            className="h-8"
          >
            <Table className="h-4 w-4" />
          </Button>
          
          {/* Link dialog */}
          <Dialog>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 flex items-center gap-1"
              >
                <LinkIcon className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Insert Link</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="linkText">Link Text</Label>
                  <Input 
                    id="linkText" 
                    value={linkText} 
                    onChange={(e) => setLinkText(e.target.value)}
                    placeholder="Text to display"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linkUrl">URL</Label>
                  <Input 
                    id="linkUrl" 
                    value={linkUrl} 
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://example.com"
                  />
                </div>
                
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button onClick={insertLink}>Insert</Button>
                  </DialogClose>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Image dialog */}
          <Dialog>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 flex items-center gap-1"
              >
                <Image className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Insert Image</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="imageAlt">Alt Text</Label>
                  <Input 
                    id="imageAlt" 
                    value={imageAlt} 
                    onChange={(e) => setImageAlt(e.target.value)}
                    placeholder="Description of the image"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imageUrl">Image URL</Label>
                  <Input 
                    id="imageUrl" 
                    value={imageUrl} 
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                
                {imageUrl && (
                  <div className="space-y-2">
                    <Label>Preview</Label>
                    <div className="overflow-hidden rounded-md border bg-muted p-2">
                      <CustomImageViewer 
                        src={imageUrl} 
                        alt={imageAlt || 'Preview'} 
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button onClick={insertImage}>Insert</Button>
                  </DialogClose>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Video embed dialog */}
          <Dialog>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 flex items-center gap-1"
              >
                <Video className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Insert Video</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="videoTitle">Video Title (optional)</Label>
                  <Input 
                    id="videoTitle" 
                    value={videoTitle} 
                    onChange={(e) => setVideoTitle(e.target.value)}
                    placeholder="Title for the video"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="youtubeVideoId">YouTube Video ID</Label>
                  <Input 
                    id="youtubeVideoId" 
                    value={youtubeVideoId} 
                    onChange={(e) => setYoutubeVideoId(e.target.value)}
                    placeholder="Enter YouTube Video ID"
                  />
                </div>
                
                {youtubeVideoId && (
                  <div className="space-y-2">
                    <Label>Preview</Label>
                    <div className="aspect-video w-full overflow-hidden rounded-md border bg-muted">
                      {youtubeVideoId.length === 11 ? (
                        <CustomYouTubePlayer videoId={youtubeVideoId} />
                      ) : (
                        <div className="flex items-center justify-center h-full p-4">
                          <p className="text-muted-foreground">Invalid YouTube Video ID</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button onClick={insertVideo}>Insert</Button>
                  </DialogClose>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* File search dialog */}
          <Dialog>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 flex items-center gap-1"
              >
                <File className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Insert File</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
                
                <div className="max-h-[300px] overflow-y-auto border rounded-md">
                  {isLoading ? (
                    <div className="p-4 text-center">Loading files...</div>
                  ) : filteredFiles.length === 0 ? (
                    <div className="p-4 text-center">No files found</div>
                  ) : (
                    <div className="divide-y">
                      {filteredFiles.map((file) => (
                        <div 
                          key={file.fileName}
                          className="p-2 hover:bg-muted flex items-center justify-between cursor-pointer"
                          onClick={() => insertFile(file)}
                        >
                          <div className="flex items-center gap-2">
                            <File className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate max-w-[200px]">{file.originalName}</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              insertFile(file);
                            }}
                          >
                            Insert
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <Textarea
          id="textContent"
          placeholder="Enter your content here... Use Markdown for formatting"
          className="min-h-[300px] border-none focus-visible:ring-0 p-0"
          value={content}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      
      {content && (
        <div className="mt-4 border rounded-md p-4">
          <Label className="mb-2 block">Preview</Label>
          <div className="prose prose-slate max-w-none dark:prose-invert">
            <div 
              dangerouslySetInnerHTML={{ 
              __html: (() => {
                // Process content in stages, similar to Lesson.tsx
                let processedContent = content;
                
                // First process video tags
                // Try to find any format of !video[...](...)
                processedContent = processedContent.replace(/!video\s*\[(.*?)\]\s*\((.*?)\)/g, (match, title, url) => {
                  const videoId = getYouTubeVideoId(url);
                  if (videoId) {
                    try {
                      // Base64 encode the title to handle spaces and special characters
                      const encodedTitle = btoa(unescape(encodeURIComponent(title)));
                      return `VIDEO_EMBED_${videoId}_TITLE_${encodedTitle}`;
                    } catch (e) {
                      console.error("Error encoding video title:", e);
                      return `VIDEO_EMBED_${videoId}_TITLE_Video`;
                    }
                  }
                  return match; // Keep the original if not a valid video
                });
                
                // Handle malformed !video tags - specifically when there's no space between !video and [title]
                processedContent = processedContent.replace(/!video(\[(.*?)\]\((.*?)\))/g, (match, bracketContent, title, url) => {
                  const videoId = getYouTubeVideoId(url);
                  if (videoId) {
                    try {
                      // Base64 encode the title to handle spaces and special characters
                      const encodedTitle = btoa(unescape(encodeURIComponent(title)));
                      return `VIDEO_EMBED_${videoId}_TITLE_${encodedTitle}`;
                    } catch (e) {
                      console.error("Error encoding video title:", e);
                      return `VIDEO_EMBED_${videoId}_TITLE_Video`;
                    }
                  }
                  return match;
                });
                
                // Catch any other potential !video tags without proper formatting
                processedContent = processedContent.replace(/!video(\[|)([^[(]*?)(\]|)(\(|)([^()]*?)(\)|)/g, (match, openBracket, title, closeBracket, openParen, url, closeParen) => {
                  // If this is a properly formatted tag that was already processed, skip it
                  if (match.match(/!video\s*\[\s*.+?\s*\]\s*\(\s*.+?\s*\)/) || match.match(/!video\[.+?\]\(.+?\)/)) {
                    return match;
                  }
                  
                  // Try to extract a URL from anywhere in the match
                  const urlMatch = match.match(/(https?:\/\/[^\s]+)/);
                  if (urlMatch) {
                    const extractedUrl = urlMatch[1];
                    const videoId = getYouTubeVideoId(extractedUrl);
                    if (videoId) {
                      // Use the text before the URL as the title, or "Video" if none
                      const extractedTitle = title.trim() || "Video";
                      try {
                        // Base64 encode the title to handle spaces and special characters
                        const encodedTitle = btoa(unescape(encodeURIComponent(extractedTitle)));
                        return `VIDEO_EMBED_${videoId}_TITLE_${encodedTitle}`;
                      } catch (e) {
                        console.error("Error encoding video title:", e);
                        return `VIDEO_EMBED_${videoId}_TITLE_Video`;
                      }
                    }
                  }
                  return match;
                });
                
                // Also check for direct YouTube links in the content
                processedContent = processedContent.replace(/(\s|^)(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(\s|$)/g, 
                  (match, before, protocol, www, domain, videoId, after) => {
                    const fullUrl = `${protocol || 'https://'}${www || ''}${domain}${videoId}`;
                    const extractedId = getYouTubeVideoId(fullUrl);
                    if (extractedId) {
                      try {
                        // Keep the surrounding whitespace
                        const encodedTitle = btoa(unescape(encodeURIComponent("Video")));
                        return `${before}VIDEO_EMBED_${extractedId}_TITLE_${encodedTitle}${after}`;
                      } catch (e) {
                        console.error("Error encoding video title:", e);
                        return `${before}VIDEO_EMBED_${extractedId}_TITLE_Video${after}`;
                      }
                    }
                    return match;
                  }
                );
                  
                  // Process images - replace standard markdown image syntax with custom placeholders
                  processedContent = processedContent.replace(/!\[(.+?)\]\((.+?)\)/g, (match, alt, src) => {
                    try {
                      // Base64 encode the alt and src to handle spaces and special characters
                      const encodedAlt = btoa(unescape(encodeURIComponent(alt || '')));
                      const encodedSrc = btoa(unescape(encodeURIComponent(src)));
                      return `IMAGE_EMBED_${encodedSrc}_ALT_${encodedAlt}`;
                    } catch (e) {
                      console.error("Error encoding image data:", e);
                      return match; // Keep the original if there's an error
                    }
                  });
                
                // Now process the rest of markdown
                processedContent = processedContent
                  .replace(/^# (.+)$/gm, '<h1>$1</h1>')
                  .replace(/^## (.+)$/gm, '<h2>$1</h2>')
                  .replace(/^### (.+)$/gm, '<h3>$1</h3>')
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\*([^*]+)\*/g, '<em>$1</em>')
                  .replace(/__(.+?)__/g, '<u>$1</u>')
                  .replace(/^- (.+)$/gm, '<li>$1</li>')
                  .replace(/\n\n/g, '<br><br>')
                  .replace(/```(.+?)```/gs, '<pre><code>$1</code></pre>')
                  .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>')
                  .replace(/!\[(.+?)\]\((.+?)\)/g, '<img src="$2" alt="$1" class="rounded-md max-h-96 my-4">')
                  .replace(/^>\s(.+)$/gm, '<blockquote class="border-l-4 border-primary pl-4 italic">$1</blockquote>')
                  // Handle table rendering
                  .replace(/(\|[^\n]+\|\n)(\|[-|]+\|\n)(\|[^\n]+\|.+)/gs, (match) => {
                    const rows = match.split('\n').filter(row => row.trim() !== '');
                    let tableHtml = '<table class="border-collapse w-full my-4"><thead>';
                    
                    // Header row
                    if (rows[0]) {
                      tableHtml += '<tr>';
                      const cells = rows[0].split('|').filter(cell => cell.trim() !== '');
                      cells.forEach(cell => {
                        tableHtml += `<th class="border border-gray-300 dark:border-gray-600 p-2">${cell.trim()}</th>`;
                      });
                      tableHtml += '</tr></thead><tbody>';
                    }
                    
                    // Skip the separator row (rows[1])
                    
                    // Content rows
                    for (let i = 2; i < rows.length; i++) {
                      tableHtml += '<tr>';
                      const cells = rows[i].split('|').filter(cell => cell.trim() !== '');
                      cells.forEach(cell => {
                        tableHtml += `<td class="border border-gray-300 dark:border-gray-600 p-2">${cell.trim()}</td>`;
                      });
                      tableHtml += '</tr>';
                    }
                    
                    tableHtml += '</tbody></table>';
                    return tableHtml;
                  });
                  
                  // Finally, replace the video and image placeholders with actual components
                processedContent = processedContent.replace(/VIDEO_EMBED_([a-zA-Z0-9_-]+)_TITLE_([^<\s]+)(\s|$|\<)/g, (match, videoId, encodedTitle, trailingSpace) => {
                  // Decode the title from base64
                  let title;
                  try {
                    title = decodeURIComponent(escape(atob(encodedTitle)));
                  } catch (e) {
                    console.error("Error decoding title:", e, encodedTitle);
                    title = "Video"; // Fallback if decoding fails
                  }
                  
                    // Return a div with a data attribute that will be replaced with the actual component
                    // Important: Don't include the trailing space inside the div to avoid BR rendering issues
                    return `<div class="custom-youtube-player" data-video-id="${videoId}" data-video-title="${encodeURIComponent(title)}"></div>${trailingSpace || ''}`;
                  });
                  
                  processedContent = processedContent.replace(/IMAGE_EMBED_([^_]+)_ALT_([^<\s]+)(\s|$|\<)/g, (match, encodedSrc, encodedAlt, trailingSpace) => {
                    // Decode the src and alt from base64
                    let src, alt;
                    try {
                      src = decodeURIComponent(escape(atob(encodedSrc)));
                      alt = decodeURIComponent(escape(atob(encodedAlt)));
                    } catch (e) {
                      console.error("Error decoding image data:", e, encodedSrc, encodedAlt);
                      return `<img src="${encodedSrc}" alt="Image" class="rounded-md max-h-96 my-4">${trailingSpace || ''}`; // Fallback if decoding fails
                    }
                    
                    // Return a div with data attributes that will be replaced with the actual component
                    return `<div class="custom-image-viewer" data-image-src="${encodeURIComponent(src)}" data-image-alt="${encodeURIComponent(alt)}"></div>${trailingSpace || ''}`;
                });
                
                return processedContent;
              })()
              }} 
              ref={(el) => {
                // Replace placeholder divs with actual CustomYouTubePlayer components
                if (el) {
                  const playerDivs = el.querySelectorAll('.custom-youtube-player');
                  playerDivs.forEach(div => {
                    const videoId = div.getAttribute('data-video-id');
                    const encodedTitle = div.getAttribute('data-video-title');
                    
                    if (videoId && !div.querySelector('#player-' + videoId)) {
                      // Create container for React to render into
                      const container = document.createElement('div');
                      div.appendChild(container);
                      
                      // Render the custom YouTube player component into the container
                      const title = encodedTitle ? decodeURIComponent(encodedTitle) : 'Video';
                      const root = ReactDOM.createRoot(container);
                      root.render(
                        <CustomYouTubePlayer 
                          videoId={videoId}
                          title={title}
                        />
                      );
                    }
                  });
                }

                // Replace placeholder divs with actual CustomImageViewer components
                if (el) {
                  const imageDivs = el.querySelectorAll('.custom-image-viewer');
                  imageDivs.forEach(div => {
                    const encodedSrc = div.getAttribute('data-image-src');
                    const encodedAlt = div.getAttribute('data-image-alt');
                    
                    if (encodedSrc && !div.querySelector('.image-wrapper-container')) {
                      // Create container for React to render into
                      const container = document.createElement('div');
                      div.appendChild(container);
                      
                      // Render the custom image viewer component into the container
                      const src = encodedSrc ? decodeURIComponent(encodedSrc) : '';
                      const alt = encodedAlt ? decodeURIComponent(encodedAlt) : 'Image';
                      const root = ReactDOM.createRoot(container);
                      root.render(
                        <CustomImageViewer 
                          src={src}
                          alt={alt}
                        />
                      );
                    }
                  });
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TextEditor;